// HOOKS
import { useState, useContext, useEffect } from "react";
// COMPONENTS
import TableRow from "../../row.table.component";
// CONSTANTS
import ORDERED_PART_STATUSES from "../../../../../constants/parts/orderedPartStatuses.part.constant";
import PAYMENT_METHODS from "../../../../../constants/parts/paymentMethods.part.constant";
// CONTEXTS
import { SelectedOrderedPartsContext } from "../../../../../contexts/parts/selectedOrderedParts.part.context";
import { VendorsContext } from "../../../../../contexts/vendors.context";
import { SelectedOpenPartOrdersContext } from "../../../../../contexts/parts/selectedOpenPartOrders.part.context";

const DEFAULT_FORM_FIELDS = {
  name: "",
  tickets: [],
  vendor_part_number: "",
  description: "",
  vendor: "",
  status: "",
  received_date: "",
  ordered_date: "",
  po_number: "",
  quantity: "",
  each_price: "",
  shipping_price: "",
  total_price: "",
  payment_method: "",
  tracking_number: "",
  eta: "",
  notes: "",
  createdAt: "",
  updatedAt: "",
};

const DEFAULT_CLASSNAMES = {
  tableRow: {},
};

const OrderedPartsRow = ({
  handleChange = () => {},
  handleMultiSelectChange,
  data = DEFAULT_FORM_FIELDS,
  columns,
  classNames = DEFAULT_CLASSNAMES,
}) => {
  const {
    selectedOrderedParts,
    removeSelectedOrderedPart,
    addSelectedOrderedPart,
  } = useContext(SelectedOrderedPartsContext);
  const { selectedOpenPartOrders } = useContext(SelectedOpenPartOrdersContext);
  const { vendors } = useContext(VendorsContext);
  const {
    name,
    tickets,
    vendor_part_number,
    description,
    vendor,
    status,
    received_date,
    ordered_date,
    po_number,
    quantity,
    each_price,
    shipping_price,
    total_price,
    payment_method,
    tracking_number,
    eta,
    notes,
    created_at,
    updated_at,
    _id,
  } = data;

  const formProps = {
    name: {
      type: "text",
      props: {
        "data-id": _id,
        label: "Name",
        value: name,
        name: "name",
        onChange: handleChange,
        className: "w-96",
      },
    },
    status: {
      type: "selection",
      props: {
        "data-id": _id,
        label: "Status",
        value: status,
        name: "status",
        options: ORDERED_PART_STATUSES,
        className: "w-96",
        onChange: handleChange,
      },
    },
    vendor: {
      type: "selection",
      props: {
        "data-id": _id,
        label: "Vendor",
        value: vendor,
        name: "vendor",
        className: "w-96",
        options: vendors.map((v) => ({ value: v._id, label: v.name })),
        onChange: handleChange,
      },
    },
    vendor_part_number: {
      type: "text",
      props: {
        "data-id": _id,
        label: "Vendor Part #",
        value: vendor_part_number,
        name: "vendor_part_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    description: {
      type: "text",
      props: {
        "data-id": _id,
        label: "Part Description",
        value: description,
        name: "description",
        onChange: handleChange,
        className: "w-96",
      },
    },
    po_number: {
      type: "text",
      props: {
        "data-id": _id,
        label: "PO Number",
        value: po_number,
        name: "po_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    tickets: {
      type: "multiSelect",
      props: {
        label: "Tickets",
        name: "tickets",
        value: tickets,
        childValue: "ticket",
        link: {
          href: `https://techcyclesolutions.repairshopr.com/tickets`,
          identifier: "ticket_id",
          target: "_blank",
          active: true,
        },
        multiple: true,
        parentId: _id,
        options: selectedOpenPartOrders.map((part) => ({
          id: part._id,
          label: `${part.ticket_number}: ${part.description}`,
          ticket: part.ticket_number,
          description: part.description,
          ticket_id: part?.ticket_id,
        })),
        onChange: handleMultiSelectChange,
      },
    },
    quantity: {
      type: "number",
      props: {
        "data-id": _id,
        label: "Quantity",
        value: quantity,
        name: "quantity",
        onChange: handleChange,
        className: "w-48",
      },
    },
    each_price: {
      type: "number",
      props: {
        "data-id": _id,
        label: "Price/Each",
        value: each_price,
        name: "each_price",
        type: "currency",
        step: "0.01",
        onChange: handleChange,
        className: "w-48",
      },
    },
    shipping_price: {
      type: "number",
      props: {
        "data-id": _id,
        label: "Shipping Price",
        value: shipping_price,
        name: "shipping_price",
        type: "currency",
        step: "0.01",
        onChange: handleChange,
        className: "w-48",
      },
    },
    total_price: {
      type: "number",
      props: {
        "data-id": _id,
        label: "Total Price",
        value: total_price,
        name: "total_price",
        type: "currency",
        step: "0.01",
        onChange: handleChange,
        className: "w-48",
        readOnly: true,
      },
    },
    payment_method: {
      type: "selection",
      props: {
        "data-id": _id,
        label: "Payment Method",
        value: payment_method,
        name: "payment_method",
        options: PAYMENT_METHODS,
        onChange: handleChange,
        className: "w-48",
      },
    },
    tracking_number: {
      type: "text",
      props: {
        "data-id": _id,
        label: "Tracking",
        value: tracking_number,
        name: "tracking_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    eta: {
      type: "date",
      props: {
        "data-id": _id,
        label: "ETA",
        value: eta,
        name: "eta",
        onChange: handleChange,
        className: "w-48",
      },
    },
    ordered_date: {
      type: "datetime",
      props: {
        "data-id": _id,
        label: "Ordered Date",
        value: ordered_date,
        name: "ordered_date",
        onChange: handleChange,
        className: "w-48",
      },
    },
    notes: {
      type: "text",
      props: {
        "data-id": _id,
        label: "Notes",
        value: notes,
        name: "notes",
        onChange: handleChange,
      },
    },
    created_at: {
      type: "datetime",
      props: {
        "data-id": _id,
        label: "Created At",
        value: created_at,
        name: "created_at",
        className: "w-48",
        readOnly: true,
      },
    },
    updated_at: {
      type: "datetime",
      props: {
        "data-id": _id,
        label: "Updated At",
        value: updated_at,
        name: "updated_at",
        className: "w-48",
        readOnly: true,
      },
    },
    received_date: {
      type: "datetime",
      props: {
        "data-id": _id,
        label: "Received Date",
        value: received_date,
        name: "received_date",
        onChange: handleChange,
        className: "w-48",
      },
    },
  };
  const showColumn = (column) => {
    return column.show && formProps[column.id];
  };
  const handleSelect = (event) => {
    const { _id } = event.target.dataset;
    const selectedOrderedPart = selectedOrderedParts.find(
      (part) => part._id === _id
    );
    if (selectedOrderedPart) {
      removeSelectedOrderedPart(_id);
    } else {
      addSelectedOrderedPart(data);
    }
  };
  return (
    <TableRow
      data={data}
      checked={!!selectedOrderedParts.find((part) => part._id === data._id)}
      handleSelect={handleSelect}
      select={true}
      classNames={classNames.tableRow}
      fields={columns.filter(showColumn).map((column) => formProps[column.id])}
    />
  );
};

export default OrderedPartsRow;
